// import { Link } from "@tanstack/react-router";
import { Button } from "@workspace/web-ui/components/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
// import { cn } from "@workspace/web-ui/lib/utils";
import { CircleCheck, Clock, Info, PlusIcon } from "lucide-react";
import { useState } from "react";

import { Tasks } from "~/features/dashboard/components/tasks";
import TaskContainer from "~/features/dashboard/components/tasks/task-container";
import { NotesContainer } from "~/features/dashboard/components/notes";
import { FormattedEncounter } from "../../../encounters/types";
import { PatientDetailsConditional } from "~/features/dashboard/components/patient/patient-details-conditional";
import { getRouteApi } from "@tanstack/react-router";
import { ConsultationStatus } from "../consultation-status";
import { useSendActionNeededMutation } from "../../../patient-logs/api/mutations";
import { toast } from "sonner";
import { ResolveToggle } from "~/features/dashboard/components/resolve-toggle";
// import { Sheet, SheetContent } from "@workspace/web-ui/components/sheet";
// import { useQuery } from "@tanstack/react-query";
// import { patientMedicalPlanQueryOptions } from "../../../patient-logs/api/queries";
// import Questionnaire from "../patient-log/tab-content/questionnaire";

interface ConsultationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    encounter: FormattedEncounter;
    onClose?: () => void;
}

export const QUESTION_LABELS: Record<string, string> = {
    awareness: "Are you aware of the program?",
    "daily-injections": "Are you comfortable with daily injections?",
    "stomach-issues": "Do you have any stomach issues?",
    "why-semaglutide": "Why did you choose Semaglutide?",
    // Add more mappings as needed
};

export const ConsultationModal = (props: ConsultationModalProps) => {
    const { open, onOpenChange, encounter } = props;
    const [isTaskCardOpen, setIsTaskCardOpen] = useState(false);
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();

    // const { data: medicalPlanData, isPending : PatientMedicalPlanLoading } = useQuery(
    //     patientMedicalPlanQueryOptions(encounter.patient.id)
    // );

    // console.log("medical_plan", medicalPlanData?.program_details.plan.supplementary_questionnaire)

    // Check for medication_payment tasks to determine resolved/action needed status
    const normalizedTasks = Array.isArray(encounter.recommendedTasks) ? encounter.recommendedTasks : [];
    const medicationPaymentTasks = normalizedTasks.filter(
        (task: any) => task?.type === "medication_payment"
    );

    // There may be multiple medication_payment tasks.
    const anyCompletedPayment = medicationPaymentTasks.some(
        (task: any) => task?.status === "completed"
    );
    const anyNotCompletedPayment = medicationPaymentTasks.some(
        (task: any) => task?.status !== "completed"
    );

    // Find the first completed task for erx check
    const completedPaymentTask = medicationPaymentTasks.find(
        (task: any) => task?.status === "completed"
    );

    // showResolvedTag only if ALL are completed and is_erx_sent is true for the completed one.
    // showActionNeededTag if ANY are incomplete, OR if all are completed but is_erx_sent is NOT true for the completed one.
    const showResolvedTag = completedPaymentTask?.is_erx_sent === true && !anyNotCompletedPayment;
    const showActionNeededTag =
        (anyNotCompletedPayment && anyCompletedPayment) || // some completed, some not
        (anyCompletedPayment && !anyNotCompletedPayment && completedPaymentTask?.is_erx_sent !== true); // all completed, but is_erx_sent is NOT true
    const showInProgressTag =
        !anyCompletedPayment && anyNotCompletedPayment;
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                title="consultation-encounter"
                side="right"
                className="flex flex-col gap-6 overflow-y-auto p-6 sm:max-w-[620px] w-full focus:outline-none"
                aria-labelledby="consultation-details-title"
            >
                <div className="flex flex-col h-full items-start gap-4 mb-5">
                    <div className="flex flex-col gap-2 w-full flex-wrap mt-4">
                        {/* <div
                            className={cn(
                                "text-xs px-2 py-1 rounded-full flex gap-2 items-center w-fit bg-[#FFA7261A] text-[#FFA726]",
                            )}
                            aria-label={`consultation status: urgent`}
                        >
                            <span>Urgent</span>
                        </div> */}
                        <div className="flex items-center justify-between w-full">
                            <div className="flex gap-2 items-center">
                                <ConsultationStatus tag="Consultation" program={program} />
                                {showResolvedTag && (
                                    <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#16A34A] bg-[#ECFDF3] border border-[#BAE6C3] w-fit flex items-center gap-1">
                                        <CircleCheck className="size-[16px] text-[#16A34A]" />
                                        Resolved
                                    </span>
                                )}
                                {showActionNeededTag && (
                                    <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#FFA726] bg-[#FFF5E6] w-fit flex items-center gap-1">
                                        <Info className="size-[16px] text-[#FFA726]" />
                                        Action needed
                                    </span>
                                )}
                                {showInProgressTag && (
                                    <span className="text-xs font-medium py-[4px] px-[6px] rounded-full text-[#2563EB] bg-[#EFF6FF] w-fit flex items-center gap-1">
                                        <Clock className="size-[16px] text-[#2563EB]" />
                                        In progress
                                    </span>
                                )}
                            </div>
                            <ResolveToggle
                                encounterId={encounter.encounterId}
                                recommendedTasks={encounter.recommendedTasks}
                                onSuccess={() => onOpenChange(false)}
                            />
                        </div>

                        <SheetHeader className="flex">
                            <SheetTitle id="consultation-details-title" className="text-xl font-medium flex flex-row gap-2 sm:gap-3 sm:items-center items-start">{encounter.title}
                                {program === "urgent-care" ? (
                                    encounter.patient.urgent_care_onboarded && encounter.patient.identity_verified && (
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <img
                                                src="/icons/check.svg"
                                                alt="timeline"
                                                className="w-4 h-4 sm:w-5 sm:h-5"
                                            />
                                        </div>
                                    )
                                ) : (
                                    encounter.patient.medical_plan?.payment_status === "paid" &&
                                    encounter.patient.glp1_guide_accepted &&
                                    encounter.patient.identity_verified &&
                                    encounter.patient.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                        <div className="flex-shrink-0 mt-1 sm:mt-0">
                                            <img
                                                src="/icons/check.svg"
                                                alt="timeline"
                                                className="w-4 h-4 sm:w-5 sm:h-5"
                                            />
                                        </div>
                                    )
                                )}
                            </SheetTitle>
                        </SheetHeader>
                    </div>

                    {program === "urgent-care" ? (
                        <div className="flex gap-8 w-full items-start mb-4">
                            <div className="flex flex-col space-y-1 w-1/2">
                                <p className="text-[#464B55] text-xs">Submitted:</p>
                                <p className="text-[#1A202C] text-sm font-medium">{encounter.createdAt}</p>
                            </div>
                            <div className="flex flex-col space-y-1 w-1/2">
                                <p className="text-[#464B55] text-xs">Program:</p>
                                <p className="text-[#1A202C] text-sm font-medium">
                                    Urgent Care
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-8 w-full items-start mb-4">
                            <div className="flex flex-col space-y-1 w-1/2">
                                <p className="text-[#464B55] text-xs">Request Date:</p>
                                <p className="text-[#1A202C] text-sm font-medium">{encounter.createdAt}</p>
                            </div>
                            <div className="flex flex-col space-y-1 w-1/2">
                                <p className="text-[#464B55] text-xs">Program:</p>
                                <p className="text-[#1A202C] text-sm font-medium">
                                    Weight Loss
                                </p>
                            </div>
                            <div className="flex flex-col space-y-1 w-1/2">
                                <p className="text-[#464B55] text-xs">Consultation:</p>
                                <p className="text-[#1A202C] capitalize text-sm font-medium">{encounter.patient.medical_plan?.consultation_type}</p>
                            </div>
                        </div>
                    )}


                    <PatientDetailsConditional encounter={encounter} onOpenChange={onOpenChange} program={program} />

                    {/* {encounter.patient.urgent_care_onboarded && (
                            <NotesContainer
                                encounterId={encounter.encounterId}
                                patientId={encounter.patient.id}
                                notes={encounter.notes}
                                icd={encounter.session?.icd10_code}
                            />
                        )
                    } */}

                    {/* Notes Section */}
                    {program !== "urgent-care" &&
                        encounter.patient.medical_plan?.payment_status === "paid" &&
                        encounter.patient.glp1_guide_accepted &&
                        encounter.patient.identity_verified &&
                        encounter.patient.supplementary_health_assessment &&
                        Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                            <NotesContainer
                                encounterId={encounter.encounterId}
                                patientId={encounter.patient.id}
                            />
                        )
                    }

                    {/* Recommended Tasks */}
                    {program !== "urgent-care" && (
                        <>
                            {!(encounter.patient.medical_plan?.payment_status === "paid" &&
                                encounter.patient.glp1_guide_accepted &&
                                encounter.patient.identity_verified &&
                                encounter.patient.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0) && (
                                    <div className="flex items-center bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full">
                                        <p className="text-sm text-[#1A202C]">
                                            Patient must be verified
                                        </p>
                                        <Button
                                            className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                            onClick={async () => {
                                                try {
                                                    await sendActionNeeded({ patient_id: encounter.patient.id });
                                                    toast.success("Reminder sent to patient to complete verification.");
                                                } catch (error) {
                                                    toast.error("Failed to send reminder.");
                                                }
                                            }}
                                            disabled={sendActionpending}
                                        >
                                            {sendActionpending && (
                                                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                                </svg>
                                            )}
                                            Send a Reminder
                                        </Button>
                                    </div>
                                )}
                            {encounter.patient.medical_plan?.payment_status === "paid" &&
                                encounter.patient.glp1_guide_accepted &&
                                encounter.patient.identity_verified &&
                                encounter.patient.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                    <div className="flex justify-between items-center w-full p-3">
                                        <h2 className="font-semibold text-xl">Recommended Tasks</h2>
                                        <Button
                                            className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit ml-auto !border-none"
                                            onClick={() => setIsTaskCardOpen(true)}
                                        >
                                            <PlusIcon className="w-4 h-4 text-white" />
                                            Add
                                        </Button>
                                    </div>
                                )}
                            <div className="mb-5 w-full">
                                {isTaskCardOpen && (
                                    <Tasks
                                        open={isTaskCardOpen}
                                        onOpenChange={setIsTaskCardOpen}
                                        id={props.encounter.encounterId}
                                        className="bg-[#EFF0F2]"
                                    />
                                )}
                                <TaskContainer encounterId={props.encounter.encounterId} patientId={props.encounter.patient.id} />
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    )
}


